import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import { logger } from 'module/logger';
import { Menu } from 'module/ui/components/atoms/dropdown';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import { Popover } from 'module/ui/components/atoms/popover';
import { EditorPopoverContentProps } from 'module/ui/components/atoms/rich-text-editor/editor-popover/types';
import {
  getHeaderCellOfColumn,
  getHeaderCellOfRow,
  TableDataType,
  TableCellType,
} from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import {
  faPlus,
  faEdit,
  faArrowUp,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mergeAttributes, Node as TiptapNode } from '@tiptap/core';
import { TableHeader } from '@tiptap/extension-table';
import { Fragment } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  ReactNodeViewProps,
} from '@tiptap/react';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { TableHeaderPopover } from 'components/organisms/document-editor/extensions/table/TableHeaderPopover';
// Constants
const POPOVER_CONTENT_CLASS_NAME = 'editor-popover-content';
const ENHANCED_TABLE_HEADER_POPOVER_CLASS = 'enhanced-table-header-popover';

// Types
interface HeaderSaveParams {
  columnName: string;
  columnType: { value: TableDataType; label: string };
  aiContext: string;
  headerType: TableCellType.ROW | TableCellType.COLUMN;
  choices: SingleAndMultiSelectFieldOption[];
}

type InsertDirection = 'left' | 'right' | 'above' | 'below' | null;

// Utility functions
const updateHeaderCell = (
  editor: any,
  headerCell: { pos: number; node: any },
  params: HeaderSaveParams,
) => {
  if (!headerCell || !headerCell.node) {
    logger.error('updateHeaderCell: headerCell or headerCell.node is null', {
      error: new Error('headerCell or headerCell.node is null'),
    });

    return;
  }

  const tr = editor.state.tr;

  // Ensure we preserve existing attrs and properly set config
  const currentAttrs = headerCell.node.attrs || {};
  const newAttrs = {
    ...currentAttrs,
    config: {
      id: uuidv4(),
      name: params.columnName,
      dataType: params.columnType.value,
      aiContext: params.aiContext,
      cellType: params.headerType,
      choices: params.choices.map((choice) => choice.label),
    },
  };

  tr.setNodeMarkup(headerCell.pos, undefined, newAttrs);

  const newTextNode = headerCell.node.firstChild;

  if (newTextNode && newTextNode.text !== params.columnName) {
    const paragraph = editor.state.schema.nodes.paragraph.create(
      {},
      editor.state.schema.text(params.columnName),
    );

    const contentToWrap = Fragment.from([paragraph]);

    const { tableHeaderLeafNode } = editor.state.schema.nodes;

    if (!tableHeaderLeafNode) {
      logger.error(
        'updateHeaderCell: tableHeaderLeafNode not found in schema',
        {
          error: new Error('tableHeaderLeafNode not found in schema'),
        },
      );
      editor.view.dispatch(tr);

      return;
    }

    const wrapped = tableHeaderLeafNode.create({}, contentToWrap);

    tr.replaceWith(
      headerCell.pos + 1,
      headerCell.pos + 1 + newTextNode.nodeSize,
      wrapped,
    );
  }

  editor.view.dispatch(tr);
};

const findTableNode = (editor: any, pos: number) => {
  const $pos = editor.state.doc.resolve(pos);

  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);

    if (node && node.type && node.type.name === 'table') {
      return {
        node,
        pos: $pos.start(depth),
      };
    }
  }

  return null;
};

const isFirstColumn = (editor: any, pos: number) => {
  const tableInfo = findTableNode(editor, pos);

  if (!tableInfo) {
    return false;
  }

  const { node: tableNode, pos: tablePos } = tableInfo;
  const map = TableMap.get(tableNode);

  const cellPosInTable = pos - tablePos;
  const rect = map.findCell(cellPosInTable - 1);

  const firstColumnHeaderPos = tablePos + map.positionAt(0, 0, tableNode);
  const firstColumnHeaderNode = editor.state.doc.nodeAt(firstColumnHeaderPos);

  const secondRowHeaderPos = tablePos + map.positionAt(1, 0, tableNode);
  const secondRowHeaderNode = editor.state.doc.nodeAt(secondRowHeaderPos);

  // Check if the current column is the first column (column index 0)
  return (
    rect.left === 0 &&
    firstColumnHeaderNode.attrs?.config?.cellType === TableCellType.COLUMN &&
    secondRowHeaderNode.attrs?.config?.cellType === TableCellType.ROW
  );
};

const getHeaderCellForCurrentPosition = (editor: any, pos: number) => {
  const tableInfo = findTableNode(editor, pos);

  if (!tableInfo) {
    return null;
  }

  const { node: tableNode, pos: tablePos } = tableInfo;

  if (!tableNode) {
    return null;
  }

  const map = TableMap.get(tableNode);
  const cellPosInTable = pos - tablePos;

  try {
    const rect = map.findCell(cellPosInTable);
    const headerCellPos =
      tablePos + map.positionAt(rect.top, rect.left, tableNode);
    const headerCellNode = editor.state.doc.nodeAt(headerCellPos);

    return headerCellNode ? { pos: headerCellPos, node: headerCellNode } : null;
  } catch (error) {
    logger.error('Error finding header cell:', { error: error as Error, pos });

    return null;
  }
};

const CustomHeaderComponent: React.FC<ReactNodeViewProps<HTMLElement>> = ({
  getPos,
  editor,
}) => {
  const [showPopover, setShowPopover] = useState(false);

  // Get fresh node data on each render to avoid stale references
  const getCurrentNode = useCallback(() => {
    const pos = getPos();

    if (typeof pos !== 'number') {
      return null;
    }

    try {
      const $pos = editor.state.doc.resolve(pos);

      // Traverse up to find the table header node with config
      for (let depth = $pos.depth; depth >= 0; depth--) {
        const node = $pos.node(depth);

        if (node && node.type && node.type.name === 'tableHeader') {
          return node;
        }
      }

      return $pos.parent;
    } catch (error) {
      logger.error('Error resolving node position:', {
        error: error as Error,
        pos,
      });

      return null;
    }
  }, [getPos, editor]);

  const node = getCurrentNode();

  // Reset popover state when node changes
  useEffect(() => {
    setShowPopover(false);
  }, [node]);
  const handleGhostClick = useCallback(() => {
    if (editor.isEditable) {
      setShowPopover(true);
    }
  }, [editor.isEditable]);

  const handleHeaderClick = useCallback(() => {
    setShowPopover(true);
  }, []);

  const handleSave = useCallback(
    (
      columnName: string,
      columnType: { value: TableDataType; label: string },
      aiContext: string,
      headerType: TableCellType.ROW | TableCellType.COLUMN,
      choices: SingleAndMultiSelectFieldOption[],
    ) => {
      if (!editor || getPos === undefined) {
        return;
      }

      try {
        const pos = getPos();

        if (typeof pos !== 'number') {
          return;
        }

        // For ghost column, we can directly use the current node
        // Get the actual tableHeader node
        const $pos = editor.state.doc.resolve(pos);
        let headerCell: { pos: number; node: any } | null = null;

        // Traverse up to find the tableHeader node
        for (let depth = $pos.depth; depth >= 0; depth--) {
          const node = $pos.node(depth);

          if (node && node.type && node.type.name === 'tableHeader') {
            // Use the position before the node, not start of node
            const nodePos = $pos.before(depth);

            headerCell = { pos: nodePos, node };
            break;
          }
        }

        if (!headerCell) {
          logger.error('Could not find tableHeader node', {
            error: new Error('tableHeader node not found'),
          });

          return;
        }

        const isGhost = headerCell.node.attrs?.ghost === true;

        if (isGhost) {
          // Convert ghost column to real column
          const tr = editor.state.tr;

          const newAttrs = {
            ...headerCell.node.attrs,
            ghost: false,
            editable: true,
            config: {
              id: uuidv4(),
              name: columnName,
              dataType: columnType.value,
              aiContext: aiContext,
              cellType: headerType,
              choices: choices.map((choice) => choice.label),
            },
          };

          tr.setNodeMarkup(headerCell.pos, undefined, newAttrs);

          // Update the text content
          const paragraph = editor.state.schema.nodes.paragraph.create(
            {},
            editor.state.schema.text(columnName),
          );
          const contentToWrap = Fragment.from([paragraph]);
          const { tableHeaderLeafNode } = editor.state.schema.nodes;

          if (tableHeaderLeafNode && headerCell.node.firstChild) {
            const wrapped = tableHeaderLeafNode.create({}, contentToWrap);

            tr.replaceWith(
              headerCell.pos + 1,
              headerCell.pos + 1 + headerCell.node.firstChild.nodeSize,
              wrapped,
            );
          }

          editor.view.dispatch(tr);

          // Force editor to update view and then update all cells in the column
          setTimeout(() => {
            editor.view.updateState(editor.state);

            // Update all cells in this column with the proper data type
            if (headerType === TableCellType.COLUMN) {
              const tableInfo = findTableNode(editor, pos);

              if (tableInfo) {
                const { node: tableNode, pos: tablePos } = tableInfo;
                const map = TableMap.get(tableNode);

                // Find which column this header is in
                const headerPosInTable = headerCell.pos - tablePos;

                try {
                  const rect = map.findCell(headerPosInTable);
                  const cellTr = editor.state.tr;

                  // Update all cells in this column (skip the header row)
                  for (let row = 1; row < map.height; row++) {
                    const cellIndex = row * map.width + rect.left;
                    const cellPosInMap = map.map[cellIndex];
                    const contentPos = tablePos + 1 + cellPosInMap;

                    // Resolve position and traverse up to find the table cell node
                    const $cellPos = editor.state.doc.resolve(contentPos);

                    for (let depth = $cellPos.depth; depth >= 0; depth--) {
                      const node = $cellPos.node(depth);

                      if (node && node.type && node.type.name === 'tableCell') {
                        const cellPos = $cellPos.before(depth);

                        cellTr.setNodeMarkup(cellPos, undefined, {
                          ...node.attrs,
                          dataType: columnType.value,
                          width: '100%',
                          colKind: 'badge',
                        });
                        break;
                      }
                    }
                  }

                  editor.view.dispatch(cellTr);
                } catch (error) {
                  // If we can't find the cell position, just continue
                  logger.error('Error updating column cells:', {
                    error: error as Error,
                  });
                }
              }
            }

            editor.commands.focus();
          }, 0);

          setShowPopover(false);
        } else {
          // Not a ghost column, shouldn't happen in this handler
          logger.error('handleSave called on non-ghost column', {
            error: new Error(
              'handleSave should only be called for ghost columns',
            ),
          });
          setShowPopover(false);
        }
      } catch (error) {
        logger.error('Error saving ghost column:', { error: error as Error });
        setShowPopover(false);
      }
    },
    [editor, getPos],
  );

  const handleCurrentCellSave = useCallback(
    (
      columnName: string,
      columnType: { value: TableDataType; label: string },
      aiContext: string,
      headerType: TableCellType.ROW | TableCellType.COLUMN,
      choices: SingleAndMultiSelectFieldOption[],
    ) => {
      if (!editor || getPos === undefined) {
        return;
      }

      try {
        const pos = (getPos() ?? 0) > 0 ? (getPos() ?? 0) - 1 : 0;

        if (typeof pos !== 'number') {
          return;
        }

        editor.commands.setTextSelection(pos);
        editor.commands.focus();

        const headerCell = getHeaderCellForCurrentPosition(editor, pos);

        if (!headerCell) {
          return;
        }

        const tableInfo = findTableNode(editor, pos);

        if (!tableInfo) {
          return;
        }

        const { node: tableNode, pos: tablePos } = tableInfo;
        const map = TableMap.get(tableNode);
        const cellPosInTable = pos - tablePos;
        const rect = map.findCell(cellPosInTable);

        const tr = editor.state.tr;

        if (headerType === TableCellType.COLUMN) {
          updateHeaderCell(editor, headerCell, {
            columnName,
            columnType,
            aiContext,
            headerType: TableCellType.COLUMN,
            choices,
          });

          // Update all cells in the column
          for (let row = 1; row < map.height; row++) {
            const cellIndex = row * map.width + rect.left;
            const cellPosInMap = map.map[cellIndex];
            const cellPos = tablePos + 1 + cellPosInMap;
            const cell = editor.state.doc.nodeAt(cellPos);

            if (cell) {
              tr.setNodeMarkup(cellPos, undefined, {
                ...cell.attrs,
                width: '100%',
                colKind: 'badge',
              });
            }
          }
        } else {
          tr.setNodeMarkup(headerCell.pos, undefined, {
            ...headerCell.node.attrs,
            config: {
              id: uuidv4(),
              name: columnName,
              aiContext: aiContext,
              cellType: headerType,
            },
          });

          const newTextNode = headerCell.node.firstChild;

          const paragraph = editor.state.schema.nodes.paragraph.create(
            {},
            editor.state.schema.text(columnName),
          );

          const contentToWrap = Fragment.from([paragraph]);

          const { tableHeaderLeafNode } = editor.state.schema.nodes;
          const wrapped = tableHeaderLeafNode.create({}, contentToWrap);

          if (newTextNode && newTextNode.text !== columnName) {
            tr.replaceWith(
              headerCell.pos + 1,
              headerCell.pos + 1 + newTextNode.nodeSize,
              wrapped,
            );
          }
        }

        editor.view.dispatch(tr);
        setShowPopover(false);
      } catch (error) {
        logger.error('Error updating current cell:', { error: error as Error });
        setShowPopover(false);
      }
    },
    [editor, getPos],
  );

  const hidePopover = useCallback(() => {
    setShowPopover(false);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      !(event.target as HTMLElement)?.closest(
        `.${ENHANCED_TABLE_HEADER_POPOVER_CLASS}`,
      )
    ) {
      setShowPopover(false);
    }
  }, []);

  // Content component for SelectionActionPopover
  const PopoverContent: React.FC<EditorPopoverContentProps> = ({
    closePopover,
  }) => (
    <TableHeaderPopover
      headerType={TableCellType.COLUMN}
      headerText=""
      node={node}
      onSave={handleSave}
      onCancel={() => {
        closePopover();
      }}
      handleClickOutside={handleClickOutside}
    />
  );

  const PopoverContentCurrentCell: React.FC<{
    closePopover: () => void;
    node: any;
    editor: any;
  }> = ({ closePopover, node, editor }) => {
    const [showEditPopover, setShowEditPopover] = useState(false);
    const [showInsertPopover, setShowInsertPopover] = useState(false);
    const [insertDirection, setInsertDirection] =
      useState<InsertDirection>(null);
    const [capturedCellType, setCapturedCellType] = useState<
      TableCellType.ROW | TableCellType.COLUMN
    >(node?.attrs?.config?.cellType || TableCellType.COLUMN);
    const [capturedNodeData, setCapturedNodeData] = useState<any>(null);

    // Reset states when node changes (e.g., when clicking on a different column)
    useEffect(() => {
      setShowEditPopover(false);
      setShowInsertPopover(false);
      setInsertDirection(null);
      setCapturedNodeData(null);
    }, [node]);

    // Get the actual header cell node with config
    const getActualHeaderNode = useCallback(() => {
      const pos = getPos();

      if (typeof pos !== 'number') {
        return node;
      }

      try {
        const headerCell = getHeaderCellForCurrentPosition(editor, pos);

        if (headerCell && headerCell.node) {
          return headerCell.node;
        }
      } catch (error) {
        logger.error('Error getting actual header node:', {
          error: error as Error,
        });
      }

      return node;
    }, [editor, node]);

    const handleMenuClick = useCallback(
      ({ key }: { key: string }) => {
        const pos = getPos();

        if (typeof pos !== 'number') {
          return;
        }

        // Get the actual header node with config
        const actualNode = getActualHeaderNode();

        // Capture cellType and node data before any state changes
        const currentCellType =
          actualNode?.attrs?.config?.cellType || TableCellType.COLUMN;

        setCapturedCellType(currentCellType);
        setCapturedNodeData(actualNode);

        editor.commands.setTextSelection(pos);

        switch (key) {
          case 'edit_column':
            setShowEditPopover(true);
            break;
          case 'insert_left':
            // Check if current column is first column before allowing insert left
            if (isFirstColumn(editor, pos)) {
              return; // Don't proceed if current column is first column
            }
            setInsertDirection('left');
            setShowInsertPopover(true);
            break;
          case 'insert_right':
            setInsertDirection('right');
            setShowInsertPopover(true);
            break;
          case 'insert_above':
            setInsertDirection('above');
            setShowInsertPopover(true);
            break;
          case 'insert_below':
            setInsertDirection('below');
            setShowInsertPopover(true);
            break;
          case 'delete':
            if (currentCellType === TableCellType.ROW) {
              editor.commands.deleteRow();
            } else {
              editor.commands.deleteColumn();
            }
            setInsertDirection(null);
            closePopover();
            break;
          default:
            break;
        }
      },
      [editor, closePopover, getActualHeaderNode],
    );

    const handleEditCancel = useCallback(() => {
      setShowEditPopover(false);
    }, []);

    const handleInsertCancel = useCallback(() => {
      setInsertDirection(null);
      setShowInsertPopover(false);
    }, []);

    const handleColumnInsert = useCallback(
      (
        columnName: string,
        columnType: { value: TableDataType; label: string },
        aiContext: string,
        choices: SingleAndMultiSelectFieldOption[],
      ) => {
        if (insertDirection === 'left') {
          editor.commands.addColumnBefore();
        } else if (insertDirection === 'right') {
          editor.commands.addColumnAfter();
        }

        // Use setTimeout to ensure the column insertion is complete
        setTimeout(() => {
          const headerCell = getHeaderCellOfColumn(
            editor.state,
            insertDirection as 'left' | 'right',
          );

          if (headerCell) {
            updateHeaderCell(editor, headerCell, {
              columnName,
              columnType,
              aiContext,
              headerType: TableCellType.COLUMN,
              choices,
            });

            // Force editor to update and focus
            editor.view.updateState(editor.state);
            editor.commands.focus();
          }
        }, 0);
      },
      [editor, insertDirection],
    );

    const handleRowInsert = useCallback(
      (columnName: string, aiContext: string) => {
        if (insertDirection === 'above') {
          editor.commands.addRowBefore();
        } else if (insertDirection === 'below') {
          editor.commands.addRowAfter();
        }

        // Use setTimeout to ensure the row insertion is complete
        setTimeout(() => {
          const headerCell = getHeaderCellOfRow(
            editor.state,
            insertDirection as 'above' | 'below',
          );

          if (headerCell) {
            const tr = editor.state.tr;

            // Ensure config object is properly initialized
            const currentAttrs = headerCell.node.attrs || {};
            const currentConfig = currentAttrs.config || {};

            tr.setNodeMarkup(headerCell.pos, undefined, {
              ...currentAttrs,
              config: {
                ...currentConfig,
                name: columnName,
                id: uuidv4(),
                aiContext: aiContext,
                cellType: TableCellType.ROW,
              },
            });

            const textNode = headerCell.node.firstChild;

            if (textNode && textNode.text !== columnName) {
              const paragraph = editor.state.schema.nodes.paragraph.create(
                {},
                editor.state.schema.text(columnName),
              );

              const contentToWrap = Fragment.from([paragraph]);

              const { tableHeaderLeafNode } = editor.state.schema.nodes;
              const wrapped = tableHeaderLeafNode.create({}, contentToWrap);

              tr.replaceWith(
                headerCell.pos + 1,
                headerCell.pos + 1 + textNode.nodeSize,
                wrapped,
              );
            }

            editor.view.dispatch(tr);
            editor.commands.focus();
          }
        }, 0);
      },
      [editor, insertDirection],
    );

    const handleHeaderSave = useCallback(
      (
        columnName: string,
        columnType: { value: TableDataType; label: string },
        aiContext: string,
        _headerType: TableCellType.ROW | TableCellType.COLUMN,
        choices: SingleAndMultiSelectFieldOption[],
      ) => {
        setShowInsertPopover(false);

        if (capturedCellType === TableCellType.COLUMN) {
          handleColumnInsert(columnName, columnType, aiContext, choices);
        } else {
          handleRowInsert(columnName, aiContext);
        }

        setInsertDirection(null);
        closePopover();
      },
      [closePopover, capturedCellType, handleColumnInsert, handleRowInsert],
    );

    const handleClickOutside = useCallback((event: MouseEvent) => {
      if (
        !(event.target as HTMLElement)?.closest(
          `.${ENHANCED_TABLE_HEADER_POPOVER_CLASS}`,
        )
      ) {
        setShowEditPopover(false);
        setShowInsertPopover(false);
      }
    }, []);

    const nodeData = useMemo(() => {
      if (insertDirection) {
        return {
          attrs: {
            config: {
              name: 'Column',
              dataType: TableDataType.TEXT,
              aiContext: '',
              cellType: capturedCellType,
            },
          },
        };
      }

      return node;
    }, [insertDirection, capturedCellType, node]);

    const menuItems = useMemo(() => {
      const currentNode = capturedNodeData || node;
      const isRow = currentNode?.attrs?.config?.cellType === TableCellType.ROW;
      const pos = getPos();
      const isFirstColumnPosition =
        typeof pos === 'number' ? isFirstColumn(editor, pos) : false;

      return [
        {
          type: 'group' as const,
          label: 'Actions',
          children: [
            {
              key: 'edit_column',
              label: isRow ? 'Edit row...' : 'Edit column...',
              icon: <FontAwesomeIcon icon={faEdit} />,
            },
            {
              type: 'divider' as const,
            },
            ...(isRow
              ? [
                  {
                    key: 'insert_above',
                    label: 'Insert above',
                    icon: <FontAwesomeIcon icon={faArrowUp} />,
                  },
                  {
                    key: 'insert_below',
                    label: 'Insert below',
                    icon: <FontAwesomeIcon icon={faArrowDown} />,
                  },
                ]
              : [
                  {
                    key: 'insert_left',
                    label: 'Insert left',
                    icon: <FontAwesomeIcon icon={faArrowLeft} />,
                    disabled: isFirstColumnPosition,
                  },
                  {
                    key: 'insert_right',
                    label: 'Insert right',
                    icon: <FontAwesomeIcon icon={faArrowRight} />,
                  },
                ]),
            {
              type: 'divider' as const,
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <FontAwesomeIcon icon={faTrash} />,
              danger: true,
            },
          ],
        },
      ];
    }, [capturedNodeData, node, editor]);

    // If showing edit popover, render it instead of the dropdown
    if (showEditPopover) {
      const editNode = capturedNodeData || node;

      return (
        <TableHeaderPopover
          headerType={capturedCellType}
          headerText=""
          node={editNode}
          onSave={handleCurrentCellSave}
          onCancel={handleEditCancel}
          handleClickOutside={handleClickOutside}
        />
      );
    }

    // If showing insert popover, render it instead of the dropdown
    if (showInsertPopover) {
      return (
        <TableHeaderPopover
          headerType={capturedCellType}
          headerText=""
          node={nodeData}
          onSave={handleHeaderSave}
          onCancel={handleInsertCancel}
          handleClickOutside={handleClickOutside}
        />
      );
    }

    // Show the actions dropdown menu
    return (
      <div className="table-cell-actions-dropdown">
        <Menu items={menuItems} onClick={handleMenuClick}>
          <NodeViewContent />
        </Menu>
      </div>
    );
  };

  const contentClass = useMemo(() => {
    return [POPOVER_CONTENT_CLASS_NAME].filter(Boolean).join(' ');
  }, []);

  const isOutsideClickAllowed = useCallback(
    (target: HTMLElement) => {
      return (
        !!target.closest(`.${POPOVER_CONTENT_CLASS_NAME}`) ||
        contentClass.split(' ').some((name) => !!target.closest(`.${name}`))
      );
    },
    [contentClass],
  );

  // Early return if node is not available
  if (!node) {
    return (
      <NodeViewWrapper className="node-view-table">
        <div className="table-ghost-wrapper">
          <div className="table-ghost">
            <FontAwesomeIcon icon={faPlus} />
            <span>Loading...</span>
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="node-view-table">
      <div className={node.attrs.ghost === true ? 'table-ghost-wrapper' : ''}>
        {node.attrs.ghost ? (
          <OutsideClickHandler
            allowed={isOutsideClickAllowed}
            onOutsideClick={hidePopover}
          >
            <Popover
              content={
                <div className={contentClass}>
                  <PopoverContent
                    editor={editor}
                    closePopover={() => {
                      setShowPopover(false);
                    }}
                  />
                </div>
              }
              open={showPopover}
            >
              <div className={'table-ghost'} onClick={handleGhostClick}>
                <FontAwesomeIcon icon={faPlus} />
                <span>Add a column</span>
              </div>
            </Popover>
          </OutsideClickHandler>
        ) : (
          <OutsideClickHandler
            allowed={isOutsideClickAllowed}
            onOutsideClick={hidePopover}
          >
            <Popover
              content={
                <div className={contentClass}>
                  <PopoverContentCurrentCell
                    key={`${getPos()}-${showPopover}`}
                    editor={editor}
                    node={node}
                    closePopover={() => {
                      setShowPopover(false);
                    }}
                  />
                </div>
              }
              open={showPopover}
            >
              <div
                className="node-view-table-content"
                onClick={editor.isEditable ? handleHeaderClick : undefined}
              >
                <NodeViewContent />
              </div>
            </Popover>
          </OutsideClickHandler>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export const tableHeaderLeafNode = TiptapNode.create({
  name: 'tableHeaderLeafNode',

  group: 'block', // block-level node again
  content: 'block+',

  parseHTML() {
    return [{ tag: 'div[data-type="tableHeaderLeafNode"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'tableHeaderLeafNode' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CustomHeaderComponent, {
      className: 'table-tiptap',
    });
  },
});

export const CustomTableHeader = TableHeader.extend<{ editable: boolean }>({
  content: 'tableHeaderLeafNode | paragraph+',
  addAttributes() {
    return {
      ...this.parent?.(),
      config: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('data-header-config');

          if (!raw) {
            return null;
          }
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        },
        renderHTML: (attrs) =>
          attrs.config
            ? { 'data-header-config': JSON.stringify(attrs.config) }
            : {},
      },
      editable: {
        default: this.options.editable,
        parseHTML: (element) =>
          this.options.editable ? false : element.getAttribute('readonly'),
        renderHTML: (attrs) => {
          return { readonly: this.options.editable ? false : attrs.readonly };
        },
      },
      ghost: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-ghost') === 'true',
        renderHTML: (attrs) => (attrs.ghost ? { 'data-ghost': 'true' } : {}),
      },
    };
  },
});
